import { useState, useEffect } from "react";
import { Check, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

/**
 * Combobox con buscador para seleccionar un banco argentino.
 * Props:
 *  - value: { code, name } | null
 *  - onChange: (b) => void
 *  - disabled: boolean
 *  - testId: string
 */
export default function BancoCombobox({ value, onChange, disabled, testId = "banco-combobox" }) {
  const [open, setOpen] = useState(false);
  const [bancos, setBancos] = useState([]);

  useEffect(() => {
    if (bancos.length === 0) {
      api.get("/cheques/bancos").then(({ data }) => setBancos(data || [])).catch(() => {});
    }
  }, [bancos.length]);

  const selectedLabel = value?.name || "Seleccionar banco emisor…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className="w-full justify-between rounded-sm h-9 font-mono text-[12px]"
        >
          <span className={cn("truncate text-left", !value && "text-[#999]")}>
            {selectedLabel}
          </span>
          <CaretDown size={12} weight="bold" className="ml-2 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[92vw] sm:w-[420px] sm:max-w-[420px] p-0" align="start">
        <Command shouldFilter={true}>
          <div className="flex items-center px-2 border-b">
            <MagnifyingGlass size={14} className="text-[#999] mr-2" />
            <CommandInput
              data-testid={`${testId}-search`}
              placeholder="Buscar por nombre o código…"
              className="h-9 border-0 focus:ring-0 text-[12px]"
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>No hay resultados.</CommandEmpty>
            <CommandGroup>
              {bancos.map((b) => (
                <CommandItem
                  key={b.code}
                  data-testid={`${testId}-item-${b.code}`}
                  value={`${b.code} ${b.name}`}
                  onSelect={() => {
                    onChange(b);
                    setOpen(false);
                  }}
                  className="text-[12px] font-mono"
                >
                  <Check
                    size={12}
                    className={cn(
                      "mr-2",
                      value?.code === b.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-[#999] mr-2 tabular-nums">{b.code}</span>
                  <span className="truncate">{b.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
